import { useEffect, useState, ReactNode } from 'react';
import useGoogleClientId from 'hooks/useGoogleClientId';
import logOneTapDisplayed from '@alltrails/analytics/events/logOneTapDisplayed';
import { LanguageSupportUtil } from 'utils/language_support_util';
import { googleHandler, appleRedirectHandler } from 'utils/OAuthHandlers';
import useUser from 'hooks/useUser';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import CustomProvider from 'components/CustomProvider';
import useFeatures from 'hooks/useFeatures';
import NavigatorModal from '../MarketingModal/NavigatorModal';
import AppInstallModal from '../MarketingModal/AppInstallModal';
import logError from '../../utils/logError';
import useMobileWidth from '../../hooks/useMobileWidth';

type GlobalActionsProps = {
  signupModalIsOpen: boolean;
};

const GlobalActions = ({ signupModalIsOpen }: GlobalActionsProps) => {
  const community = useFeatures().includes('community');
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const googleClientId = useGoogleClientId();
  const [children, setChildren] = useState<ReactNode>(null);
  const isMobileWidth = useMobileWidth();

  // In testing the pendo script tag has been slow to load so we
  // attempt up to 4 times every 750 ms to initialize the user.
  let pendoSetUpAttempts = 0;
  const maxPendoSetUpAttempts = 3;
  const pendoSetUpBackOff = 750;

  const setUpPendo = () => {
    if (!user || pendoSetUpAttempts > maxPendoSetUpAttempts) {
      return;
    }

    if (window.pendo) {
      try {
        const visitor = {
          id: user.id,
          country: user.location.country,
          first_name: user.firstName,
          preferred_language: user.marketingLangPreference,
          pro: user.pro,
          pro_conversion_dt: user.subscription?.startDate,
          account_create_dt: user.created,
          is_subscribed: !user.email_none,
          is_community_beta_enabled: community
        };
        window.pendo.initialize({ visitor });
      } catch (e) {
        logError(e);
      }
    } else {
      pendoSetUpAttempts += 1;
      setTimeout(setUpPendo, pendoSetUpBackOff);
    }
  };

  useEffect(() => {
    setUpPendo();
  }, []);

  useEffect(() => {
    function showMarketingModal() {
      const searchParams = new URLSearchParams(window.location.search);

      if (searchParams.has('navigator_desktop')) {
        setChildren(<NavigatorModal />);
      }

      if (searchParams.has('appinstall_desktop')) {
        setChildren(<AppInstallModal />);
      }
    }

    showMarketingModal();
  }, []);

  useEffect(() => {
    function callback(resp: any) {
      googleHandler(
        {
          getAuthResponse: () => ({
            id_token: resp.credential
          })
        },
        languageRegionCode,
        (response: any) => {
          if (response.welcome_notice) {
            const params = new URLSearchParams(window.location.search);
            params.set('welcome', response.welcome_notice);
            const search = params.toString();
            window.location.replace(window.location.href + (search !== '' ? `?${search}` : ''));
          } else {
            window.location.replace(window.location.href);
          }
        },
        logError,
        () => {},
        null, // TODO: This should be passed-in
        true
      );
    }

    const setUpGoogleOneTap = () => {
      window.google.accounts.id.initialize({
        callback,
        cancel_on_tap_outside: false,
        client_id: googleClientId,
        prompt_parent_id: 'g_one_tap'
      });

      if (!signupModalIsOpen) {
        window.google.accounts.id.prompt((notification: any) => {
          if (notification.isDisplayMoment() && notification.isDisplayed()) {
            logOneTapDisplayed();
          }
        });
      }
    };

    if (
      !user &&
      !isMobileWidth &&
      !['/about', '/careers', '/pro', '/signup', '/login'].some(
        path => LanguageSupportUtil.wrapUrlSafe(path, languageRegionCode) === window.location.pathname
      )
    ) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => setUpGoogleOneTap();
      document.body.appendChild(script);
    }
  }, [user, googleClientId, isMobileWidth, languageRegionCode]);

  useEffect(() => {
    appleRedirectHandler();
  }, []);

  // wrap return in a fragment to make TypeScript happy
  return <>{children}</>;
};

function GlobalActionsWrapper({ signupModalIsOpen }: GlobalActionsProps) {
  return (
    <CustomProvider>
      <GlobalActions signupModalIsOpen={signupModalIsOpen} />
    </CustomProvider>
  );
}

export default GlobalActionsWrapper;
